import React, { useState, useEffect, CSSProperties } from 'react';
import { Button, Popover } from '@barges/react';

import '@barges/scss/lib/popover.css';
import { TFunction } from 'i18next';

import LoadingIndicator from '@common/helpers/loading_indicator';
import { useLocalizedNavigate } from '@app/components/routes/hooks/useLocalizedNavigate';
import { appRoutes } from '@app/config/app_routes';
import { SvgIcon } from '@common/sprites';
import classes from './classes.module.scss';
import withPGTranslation from '../../config/withPGTranslation';
import { parseAndTrackAnalyticEvents } from '../../actions/analytics';

export interface MainAdvisorBonusProps extends React.PropsWithChildren {
  t: TFunction;
  user: User.Details;
  isOpen: boolean;
  advisorId: string;
  skipBtnHandler?: (params?: object) => void;
  onCancel: () => void;
  initialLoadedHandler?: () => void;
}

const popoverStyles: CSSProperties = {
  padding: 25,
  position: 'fixed',
  rowGap: 0
};
const eventName = 'customer skipped bonus';

const AdvisorBonusWrapper = (props: MainAdvisorBonusProps) => {
  const {
    t, user, isOpen, children, advisorId, skipBtnHandler, onCancel, initialLoadedHandler
  } = props;
  const [paymentProcessingId, setPaymentProcessingId] = useState<NodeJS.Timeout>();
  const [displaySkipProcessing, setDisplaySkipProcessing] = useState(false);
  const [isSkipped, setIsSkipped] = useState(false);
  const history = useLocalizedNavigate();

  useEffect(() => {
    if (isOpen && user?.paymentProcessing) {
      setPaymentProcessingId(
        setTimeout(() => {
          setDisplaySkipProcessing(true);
        }, 10000)
      );
      if (initialLoadedHandler) initialLoadedHandler();
    }
    if (!user?.paymentProcessing && paymentProcessingId) {
      clearTimeout(paymentProcessingId);
      setPaymentProcessingId(null);
    }
  }, [user?.paymentProcessing, isOpen]);

  const userProcessingPopoverOnSkipClick = () => {
    clearTimeout(paymentProcessingId);
    const analyticProperties = { [eventName]: { 'processing payment': user?.paymentProcessing } };
    if (advisorId) {
      parseAndTrackAnalyticEvents(analyticProperties);
      history(appRoutes.advisorOrders, { params: { id: advisorId }, options: { replace: true } });
    } else {
      skipBtnHandler({ analytic_events: analyticProperties });
    }
    setPaymentProcessingId(null);
    setIsSkipped(true);
    onCancel();
  };
  const renderUserProcessingPopover = () => (
    <Popover
      isOpen
      renderTitle={ () => <></> }
      key="user-processing-popover"
      centered
      style={ { ...popoverStyles, backgroundColor: 'black', width: 250 } }
    >
      <div className={ classes.userProcessingPopoverContainer }>
        <div className={ classes.userProcessingPopoverTextContainer }>
          <LoadingIndicator />
          <div className={ classes.userProcessingPopoverText }>
            { t('advisor_bonus.processing_payment') }
          </div>
        </div>
        { displaySkipProcessing && (
          <div className={ classes.userProcessingPopoverSkipButtonContainer }>
            <Button
              type="link"
              onClick={ userProcessingPopoverOnSkipClick }
              title={ t('advisor_bonus.skip') }
            />
          </div>
        )}
      </div>
    </Popover>
  );

  const errorProcessingPopoverOnOkClick = () => {
    if (advisorId) {
      history(appRoutes.advisorOrders, { params: { id: advisorId }, options: { replace: true } });
    } else {
      skipBtnHandler();
    }
    onCancel();
  };

  const renderErrorProcessingPopover = () => (
    <Popover
      isOpen
      renderTitle={ () => <></> }
      key="user-error-processing-popover"
      centered
      style={ {
        ...popoverStyles, width: 343, backgroundColor: 'var(--app-background-secondary-color)'
      } }
    >
      <div className={ classes.processingPopoverErrorContainer }>
        <div className={ classes.bonusSentPopoverErrorIconContainer }>
          <SvgIcon
            id="advisor_bonus_error_icon"
            className={ classes.bonusSentPopoverErrorIcon }
          />
        </div>
        <div className={ classes.bonusSentTextContainer }>
          <div className={ classes.bonusSentText }>
            { t('advisor_bonus.error_processing') }
          </div>
        </div>
        <div className={ classes.processingPopoverOkButtonContainer }>
          <button
            type="button"
            className={ classes.bonusSentPopoverOkButton }
            onClick={ errorProcessingPopoverOnOkClick }
          >
            { t('general.ok') }
          </button>
        </div>
      </div>
    </Popover>
  );

  const renderAdvisorBonusContainer = () => {
    if (paymentProcessingId) return renderUserProcessingPopover();
    if (isSkipped) return null;
    if (isOpen && !user?.paymentProcessing && parseFloat(user?.clientAvailableCredit) < 0) return renderErrorProcessingPopover();

    return children;
  };

  return renderAdvisorBonusContainer();
};

export default withPGTranslation(AdvisorBonusWrapper);
